import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import constant from '../../constant';
import DoctorForm from '../ui/doctorForm';

const EditDoctor = () => {
  const { id, hospitalId } = useParams();
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState(null);
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDoctor = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('No authentication token found');
          return;
        }

        const response = await fetch(`${constant.REACT_APP_API_URL}/hospital/${hospitalId}/doctor/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const result = await response.json();

        if (response.ok) {
          const data = result;
          setInitialValues({
            doctor_id: data.doctor_id,
            name: data.name,
            email: data.email,
            mobile: data.mobile,
            is_active: data.is_active,
          });
        } else {
          setError(result.message || 'Failed to fetch doctor data');
        }
      } catch (error) {
        setError('Error fetching doctor data');
      }
    };

    const fetchHospitals = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('No authentication token found');
          return;
        }

        const response = await fetch(`${constant.REACT_APP_API_URL}/hospital/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const result = await response.json();
        if (response.ok) {
          setHospitals(result);
        } else {
          setError(result.message || 'Failed to fetch hospitals');
        }
      } catch (error) {
        setError('Error fetching hospitals');
      }
    };

    Promise.all([fetchDoctor(), fetchHospitals()]).finally(() => setLoading(false));
  }, [id, hospitalId]);

  const handleCancel = () => {
    navigate(-1);
  };

  const handleSubmit = async (doctorData) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const apiData = {
        name: doctorData.name,
        email: doctorData.email,
        mobile: doctorData.mobile,
        is_active: doctorData.is_active,
      };

      const response = await fetch(`${constant.REACT_APP_API_URL}/hospital/${hospitalId}/doctor/${id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData),
      });

      const result = await response.json();
      if (response.ok) {
        navigate(-1);
      } else {
        setError(result.message || 'Failed to update doctor');
      }
    } catch (error) {
      setError('Error updating doctor');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-10 text-red-500">{error}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 pt-20 md:pt-6">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center space-x-4">
            <button onClick={handleBack} className="text-gray-500 hover:text-gray-700 transition-colors">
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h2 className="text-2xl font-bold text-gray-800">Edit Doctor</h2>
          </div>
        </div>

        {loading && (
          <div className="bg-blue-50 text-blue-700 p-4 rounded-lg mb-6">
            Processing...
          </div>
        )}
        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
            {error}
          </div>
        )}
        {initialValues && (
          <DoctorForm
            initialValues={initialValues}
            hospitals={hospitals}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isEditMode={true}
          />
        )}
      </div>
    </div>
  );
};

export default EditDoctor;